import React, { FC } from 'react';
import { Employer } from '../types';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  css,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { formatAddressString } from '@features/applications/components/ApplicantViewApplicantInfo/utils';
import { white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import HomeIcon from '@mui/icons-material/Home';
import dayjs from 'dayjs';
import { staticFileLocation } from '@server/constants';

type AccountBasicInfoProps = {
  account: Employer;
  isLoadingPaidCount: boolean;
  trialCount?: { object: number };
  paidCount?: { object: number };
  alertPaidText?: string;
};

const AccountBasicInfo: FC<AccountBasicInfoProps> = ({
  account,
  isLoadingPaidCount,
  trialCount,
  paidCount,
  alertPaidText,
}) => {
  const theme = useTheme();
  const today = dayjs();
  const isFutureFeaturedDate =
    account.company && account.company?.featured_end
      ? dayjs(account.company?.featured_end).isAfter(today, 'day')
      : false;
  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', sm: 'row' }}
      pt={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        pt={2}
        alignItems="center"
      >
        <Avatar
          src={
            (account.company
              ? account.company.s_logo_file
              : (account.enterprise?.s_logo_file as string)) || ''
          }
          sx={{ width: 140, height: 140 }}
          style={{ borderRadius: 8 }}
        />
        <Stack>
          {account.company ? (
            <>
              {account.company.plan === 999 ? (
                <Stack
                  mb={2}
                  spacing={3}
                  direction="row"
                  textAlign="center"
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      width: '30%',
                      textAlign: 'center',
                      color: white,
                      background: '#F4C01E',
                      borderRadius: 5,
                    }}
                  >
                    Trial
                  </Typography>
                  <Stack
                    spacing={1}
                    direction="row"
                    textAlign="center"
                    alignItems="center"
                  >
                    {trialCount?.object ? (
                      <Stack
                        spacing={1}
                        direction="row"
                        textAlign="center"
                        alignItems="center"
                      >
                        <AccessTimeIcon />
                        <Typography>{trialCount?.object} days Left</Typography>
                      </Stack>
                    ) : (
                      isLoadingPaidCount && <CircularProgress />
                    )}
                  </Stack>
                </Stack>
              ) : account.company.plan === 0 ? (
                <Stack
                  mb={2}
                  spacing={3}
                  direction="row"
                  textAlign="center"
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      width: '80px',
                      textAlign: 'center',
                      color: white,
                      background: '#a21c1d',
                      borderRadius: 5,
                    }}
                  >
                    Expired
                  </Typography>
                </Stack>
              ) : (
                <Stack
                  mb={2}
                  spacing={3}
                  direction="row"
                  textAlign="center"
                  alignItems="center"
                >
                  {paidCount?.object ? (
                    <>
                      <Typography
                        sx={{
                          width: '80px',
                          textAlign: 'center',
                          color: white,
                          background: 'green',
                          borderRadius: 5,
                        }}
                      >
                        Paid
                      </Typography>
                      <Stack
                        spacing={1}
                        direction="row"
                        textAlign="center"
                        alignItems="center"
                      >
                        <AccessTimeIcon />
                        <Typography>{paidCount?.object} days Left</Typography>
                      </Stack>
                    </>
                  ) : isLoadingPaidCount ? (
                    <CircularProgress />
                  ) : (
                    alertPaidText && (
                      <Alert sx={{ margin: '10px 0' }} severity="error">
                        {alertPaidText}
                      </Alert>
                    )
                  )}
                </Stack>
              )}
            </>
          ) : (
            account.enterprise && (
              <Stack
                mb={2}
                spacing={3}
                direction="row"
                textAlign="center"
                alignItems="center"
              >
                {paidCount?.object ? (
                  <>
                    <Stack
                      spacing={1}
                      direction="row"
                      textAlign="center"
                      alignItems="center"
                    >
                      <AccessTimeIcon />
                      <Typography>{paidCount?.object} days Left</Typography>
                    </Stack>
                  </>
                ) : isLoadingPaidCount ? (
                  <CircularProgress />
                ) : (
                  alertPaidText && (
                    <Alert sx={{ margin: '10px 0' }} severity="error">
                      {alertPaidText}
                    </Alert>
                  )
                )}
              </Stack>
            )
          )}
          <Typography
            variant="h3"
            color="secondary"
            fontWeight="bold"
            fontSize="24px"
          >
            {account.company
              ? (account.company?.s_name ?? '-')
              : account.enterprise
                ? account.enterprise.name
                : '-'}
          </Typography>
          {((account.enterprise &&
            (account?.address1 ||
              account?.city ||
              account?.state ||
              account?.zip)) ||
            account.company?.s_address1 ||
            account.company?.s_city ||
            account.company?.s_state ||
            account.company?.s_zip) && (
            <Typography
              color="text.secondary"
              fontWeight="medium"
              style={{ fontSize: '14px' }}
            >
              <HomeIcon
                color="primary"
                fontSize="inherit"
                sx={{ verticalAlign: 'middle', marginRight: 1 }}
              />
              {formatAddressString(
                account.enterprise
                  ? account
                  : {
                      address1: account.company?.s_address1,
                      city: account.company?.s_city,
                      state: account.company?.s_state,
                      zip: account.company?.s_zip,
                    },
              )}
            </Typography>
          )}

          {account?.email && (
            <Typography
              color="text.secondary"
              fontWeight={medium}
              style={{ fontSize: '14px' }}
            >
              <AlternateEmailIcon
                color="primary"
                fontSize="inherit"
                sx={{ verticalAlign: 'middle', marginRight: 1 }}
              />
              {account.email}
            </Typography>
          )}
          {account?.phone && (
            <Typography
              color="text.secondary"
              fontWeight={medium}
              style={{ fontSize: '14px' }}
            >
              <LocalPhoneIcon
                color="primary"
                fontSize="inherit"
                sx={{ verticalAlign: 'middle', marginRight: 1 }}
              />
              {account.phone}
            </Typography>
          )}
          {account.company && account?.company.s_slug ? (
            <Typography
              color="text.secondary"
              fontWeight={medium}
              style={{ fontSize: '14px' }}
            >
              <InsertLinkIcon
                color="primary"
                fontSize="inherit"
                sx={{ verticalAlign: 'middle', marginRight: 1 }}
              />
              {account?.company.s_slug}
            </Typography>
          ) : (
            account.enterprise &&
            account?.enterprise.s_slug && (
              <Typography
                color="text.secondary"
                fontWeight={medium}
                style={{ fontSize: '14px' }}
              >
                <InsertLinkIcon
                  color="primary"
                  fontSize="inherit"
                  sx={{ verticalAlign: 'middle', marginRight: 1 }}
                />
                {account?.enterprise.s_slug}
              </Typography>
            )
          )}
        </Stack>
      </Stack>
      {account.company?.featured && isFutureFeaturedDate && (
        <Box textAlign="end">
          <img
            src={`${staticFileLocation}img/featured.png`}
            css={css`
              max-width: 80px;
              width: 100%;
              margin-right: ${theme.spacing(2)};
            `}
            alt="Featured"
          />
          <Typography
            color="text.secondary"
            fontWeight="medium"
            style={{ fontSize: '14px', fontWeight: 'bold' }}
          >
            Until: {dayjs(account.company?.featured_end).format('DD/MM/YYYY')}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default AccountBasicInfo;
